// PDF Export utility
import html2pdf from 'html2pdf.js';

export const exportToPDF = async (content, title, options = {}) => {
  const {
    language = 'plaintext',
    includeLineNumbers = true,
    theme = 'dark'
  } = options;

  // Create a styled HTML element for PDF generation
  const container = document.createElement('div');
  container.style.cssText = `
    font-family: 'JetBrains Mono', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.6;
    padding: 40px;
    background: ${theme === 'dark' ? '#1a1a2e' : '#ffffff'};
    color: ${theme === 'dark' ? '#eaeaea' : '#1a1a2e'};
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    border-bottom: 3px solid ${theme === 'dark' ? '#eaeaea' : '#1a1a2e'};
    padding-bottom: 20px;
    margin-bottom: 20px;
  `;
  header.innerHTML = `
    <h1 style="margin: 0 0 10px 0; font-size: 24px; font-weight: 900; text-transform: uppercase; letter-spacing: 2px;">
      ${title || 'Untitled'}
    </h1>
    <div style="font-size: 11px; color: ${theme === 'dark' ? '#b8b8b8' : '#4a4a5a'}; text-transform: uppercase;">
      Language: ${language} | Exported from Scrawl | ${new Date().toLocaleString()}
    </div>
  `;
  container.appendChild(header);

  // Content
  const contentDiv = document.createElement('div');
  contentDiv.style.cssText = `
    background: ${theme === 'dark' ? '#16213e' : '#f8f8f8'};
    border: 3px solid ${theme === 'dark' ? '#eaeaea' : '#1a1a2e'};
    padding: 20px;
  `;

  const lines = content.split('\n');
  
  if (includeLineNumbers) {
    const table = document.createElement('table');
    table.style.cssText = 'width: 100%; border-collapse: collapse;';
    
    lines.forEach((line, index) => {
      const row = document.createElement('tr');
      row.innerHTML = `
        <td style="
          width: 50px;
          text-align: right;
          padding-right: 15px;
          color: ${theme === 'dark' ? '#7a7a7a' : '#999'};
          border-right: 2px solid ${theme === 'dark' ? '#3a3a5a' : '#ddd'};
          user-select: none;
          vertical-align: top;
        ">${index + 1}</td>
        <td style="
          padding-left: 15px;
          white-space: pre-wrap;
          word-break: break-all;
        ">${escapeHtml(line) || ' '}</td>
      `;
      table.appendChild(row);
    });
    
    contentDiv.appendChild(table);
  } else {
    const pre = document.createElement('pre');
    pre.style.cssText = 'margin: 0; white-space: pre-wrap; word-break: break-all;';
    pre.textContent = content;
    contentDiv.appendChild(pre);
  }

  container.appendChild(contentDiv);

  // Footer
  const footer = document.createElement('div');
  footer.style.cssText = `
    margin-top: 20px;
    padding-top: 15px;
    border-top: 2px solid ${theme === 'dark' ? '#3a3a5a' : '#ddd'};
    font-size: 10px;
    color: ${theme === 'dark' ? '#7a7a7a' : '#999'};
    text-align: center;
    text-transform: uppercase;
  `;
  footer.textContent = `Generated by Scrawl — ${lines.length} lines, ${content.length} characters`;
  container.appendChild(footer);

  // PDF options
  const pdfOptions = {
    margin: 10,
    filename: `${title || 'scrawl'}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      backgroundColor: theme === 'dark' ? '#1a1a2e' : '#ffffff'
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' 
    }
  };

  // Generate PDF
  try {
    await html2pdf().set(pdfOptions).from(container).save();
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

// Helper to escape HTML
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Export content as HTML file
export const exportToHTML = (content, title, options = {}) => {
  const { language = 'plaintext', theme = 'dark' } = options;
  
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title || 'Scrawl Export'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'JetBrains Mono', 'Consolas', monospace;
      background: ${theme === 'dark' ? '#1a1a2e' : '#ffffff'};
      color: ${theme === 'dark' ? '#eaeaea' : '#1a1a2e'};
      padding: 40px;
      line-height: 1.6;
    }
    .header {
      border-bottom: 3px solid ${theme === 'dark' ? '#eaeaea' : '#1a1a2e'};
      padding-bottom: 20px;
      margin-bottom: 20px;
    }
    h1 {
      font-size: 24px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 10px;
    }
    .meta {
      font-size: 12px;
      color: ${theme === 'dark' ? '#b8b8b8' : '#4a4a5a'};
      text-transform: uppercase;
    }
    .content {
      background: ${theme === 'dark' ? '#16213e' : '#f8f8f8'};
      border: 3px solid ${theme === 'dark' ? '#eaeaea' : '#1a1a2e'};
      padding: 20px;
    }
    pre {
      white-space: pre-wrap;
      word-break: break-all;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${escapeHtml(title || 'Untitled')}</h1>
    <div class="meta">Language: ${language} | Exported from Scrawl</div>
  </div>
  <div class="content">
    <pre>${escapeHtml(content)}</pre>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title || 'scrawl'}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
